import { createTheme } from '@mui/material/styles';
import colors from '../styles/abstracts/_variables.scss';

declare module '@mui/material/styles' {
  interface Theme {
    ui: {
      primary: string;
      primaryHover: string;
    };
  }
  interface ThemeOptions {
    ui?: {
      primary?: string;
      primaryHover?: string;
    };
  }
}

declare module '@mui/material/styles' {
  interface BreakpointOverrides {
    xs: false;
    sm: false;
    md: false;
    lg: false;
    xl: false;
    phone: true;
    'small-device': true;
  }
}

let root = document.documentElement;

const lightThemeVars = {
  typography: {
    htmlFontSize: 10,
    fontFamily: 'Inter, Helvetica Neue, Helvetica, Arial, sans-serif',
  },
  breakpoints: {
    // These should by in sync with scss breakpoints
    values: {
      phone: 500,
      'small-device': 900,
    },
  },
  palette: {
    primary: {
      main:
        getComputedStyle(root).getPropertyValue('--ui-primary').trim() ||
        '#4738F5',
    },
    success: {
      main: '#4CAF50',
    },
    info: {
      main: '#4738F5',
    },
    warning: {
      main: '#4738F5',
    },
    error: {
      main: '#F44336',
    },
  },
  ui: {
    primary:
      getComputedStyle(root).getPropertyValue('--ui-primary').trim() ||
      '#4738F5',
    primaryHover:
      getComputedStyle(root).getPropertyValue('--ui-primary-bg').trim() ||
      '#4738F51F',
  },
};

export const getTheme = () => {
  let initVars = JSON.parse(JSON.stringify(lightThemeVars));

  let primaryColor =
    getComputedStyle(root).getPropertyValue('--ui-primary').trim() || '#4738F5';
  initVars.palette.primary.main = primaryColor;
  initVars.ui.primary = primaryColor;
  return createTheme(initVars);
};

export const getDarkTheme = () => {
  let darkThemeVars = JSON.parse(JSON.stringify(lightThemeVars));
  let primaryColor = getComputedStyle(root)
    .getPropertyValue('--ui-primary')
    .trim();
  darkThemeVars.palette.primary.main = primaryColor;
  darkThemeVars = {
    ...darkThemeVars,
    components: {
      MuiTab: {
        styleOverrides: {
          root: {
            '&:hover': {
              backgroundColor: '#ffffff1f',
              transition: 'all 300ms ease-out',
            },
          },
        },
      },
      MuiAlert: {
        styleOverrides: {
          filledInfo: {
            backgroundColor: '#111827',
            color: '#ffffff',
          },
        },
      },
      MuiIconButton: {
        styleOverrides: {
          root: {
            display: 'flex',
            justifyContent: 'center',
            alignItems: 'center',
            '&:hover': {
              color: '#ffffff',
              backgroundColor: '#ffffff1f',
            },
          },
        },
      },
    },
  };
  return createTheme({
    typography: {
      fontFamily: 'Inter',
      htmlFontSize: 10,
    },
    palette: {
      primary: {
        main: primaryColor,
        contrastText: colors.textPrimaryDark,
      },
      secondary: {
        main: colors.textPrimaryDark,
        contrastText: colors.textPrimaryDark,
      },
      success: {
        main: colors.successContent,
        contrastText: colors.textPrimaryDark,
      },
      info: {
        main: colors.infoContent,
      },
      warning: {
        main: colors.warningContent,
        contrastText: colors.textPrimaryDark,
      },
      error: {
        main: colors.errorContent,
      },
    },
    ui: {
      primary: primaryColor,
      primaryHover: colors.uiPrimaryHover,
    },
    components: {
      MuiTab: {
        styleOverrides: {
          root: {
            '&:hover': {
              backgroundColor: colors.dividerLight,
              transition: 'all 300ms ease-out',
            },
          },
        },
      },
      MuiSelect: {
        styleOverrides: {
          select: {
            color: colors.white, // Text color
          },
          icon: {
            color: colors.white, // Dropdown arrow color
          },
        },
      },
      MuiOutlinedInput: {
        styleOverrides: {
          root: {
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            borderRadius: '4px',
            width: '100%',
            color: colors.white,

            '& .MuiOutlinedInput-input': {
              color: colors.white,
            },

            '& .MuiOutlinedInput-notchedOutline': {
              borderColor: colors.white,
            },

            '&:hover .MuiOutlinedInput-notchedOutline': {
              borderColor: colors.white,
            },

            '&.Mui-focused .MuiOutlinedInput-notchedOutline': {
              borderColor: colors.white,
            },
          },
        },
      },
      MuiInputLabel: {
        styleOverrides: {
          root: {
            color: colors.white,

            '&.Mui-focused': {
              color: colors.white,
            },
          },
        },
      },
      MuiAlert: {
        styleOverrides: {
          filledInfo: {
            backgroundColor: primaryColor,
            color: colors.white,
          },
        },
      },
      MuiIconButton: {
        styleOverrides: {
          root: {
            display: 'flex',
            color: colors.white,
            justifyContent: 'center',
            alignItems: 'center',
            '&:hover': {
              color: colors.textPrimaryDark,
              backgroundColor: colors.dividerLight,
            },
          },
        },
      },
      MuiButton: {
        styleOverrides: {
          outlined: {
            transition: 'all .2s ease-in',
            color: colors.white,
            borderColor: colors.white,
            '&:hover': {
              opacity: 0.8,
            },
          },
          contained: {
            transition: 'all .2s linear',
            backgroundColor: colors.white,
            borderColor: colors.white,
            color: primaryColor,
            '&:hover': {
              backgroundColor: colors.white,
              opacity: 0.8,
            },
          },
        },
      },
      MuiDialog: {
        styleOverrides: {
          paper: {
            backgroundColor: colors.paperPrimaryDark,
            color: colors.white, // Ensure text is readable
          },
        },
      },
      MuiPaper: {
        styleOverrides: {
          root: {
            backgroundColor: colors.paperPrimaryDark, // Ensure Paper inside Dialog also gets the color
          },
        },
      },
    },
  });
};
